import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';

const API_BASE = '/sef-state';

// ============================================
// „Podaci sa SEF-a": what the state holds about a company — customs
// declarations (JCI), input VAT, VAT calculation records, fiscal bills
// ============================================

// Column filters carry lists (`duties[]=…`) and date ranges (`customsDate[]=from&customsDate[]=to`).
const paramsSerializer = (params) => qs.stringify(params, { arrayFormat: 'brackets' });

const fileNameFrom = (disposition, fallback) => {
  const match = /filename\*=UTF-8''([^;]+)/i.exec(disposition || '');
  return match ? decodeURIComponent(match[1]) : fallback;
};

/** Companies with a SEF key the user may read. @returns {Promise<Array<{_id, name}>>} */
export const getCompanies = async () => {
  const response = await axiosClient.get(`${API_BASE}/companies`);
  return response.data.data;
};

/** Switches and per-module state of the daily job. */
export const getProfile = async (companyId) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/profile`);
  return response.data.data;
};

/** Runs the job for the company now. @param {Object} body - { modules?, history? } */
export const refresh = async (companyId, body = {}) => {
  const response = await axiosClient.post(`${API_BASE}/${companyId}/refresh`, body);
  return response.data.data;
};

/** One page of customs declarations. */
export const getCustoms = async (companyId, params) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/customs`, { params, paramsSerializer });
  return response.data.data;
};

/** One customs declaration: duties by type, payments, history of changes. */
export const getCustomsOne = async (companyId, id) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/customs/${id}`);
  return response.data.data;
};

/** One VAT calculation record as SEF sent it. */
export const getVatRecordOne = async (companyId, id) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/vat-records/${id}`);
  return response.data.data;
};

/** One received fiscal bill as SEF sent it. */
export const getFiscalBillOne = async (companyId, id) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/fiscal-bills/${id}`);
  return response.data.data;
};

/** Months the state has input VAT for. */
export const getInputVatPeriods = async (companyId) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/input-vat`);
  return response.data.data;
};

/** One month of input VAT compared with the books. */
export const getInputVatMonth = async (companyId, year, month, params) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/input-vat/${year}/${month}`, { params, paramsSerializer });
  return response.data.data;
};

/** One page of VAT calculation records (PEP). */
export const getVatRecords = async (companyId, params) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/vat-records`, { params, paramsSerializer });
  return response.data.data;
};

/** One page of received fiscal bills. */
export const getFiscalBills = async (companyId, params) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/fiscal-bills`, { params, paramsSerializer });
  return response.data.data;
};

/**
 * Excel of a list, labelled in the user's language.
 *
 * @param {'customs'|'inputVat'|'vatRecording'|'fiscalBills'} kind
 * @returns {Promise<{ blob: Blob, fileName: string }>}
 */
export const exportList = async (companyId, kind, filters, labels) => {
  const response = await axiosClient.post(
    `${API_BASE}/${companyId}/export/${kind}`,
    { filters, labels },
    { responseType: 'blob', timeout: 5 * 60 * 1000 }
  );
  return { blob: response.data, fileName: fileNameFrom(response.headers['content-disposition'], `${kind}.xlsx`) };
};

/**
 * SEF's own PDF of an invoice (`kind: 'invoice'`) or of its status changes
 * (`'statusHistory'`). `{ pending: true }` while SEF is still making it.
 *
 * @returns {Promise<{ blob: Blob, fileName: string } | { pending: true }>}
 */
export const getInvoicePdf = async (companyId, invoiceId, kind = 'invoice') => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/invoice-pdf/${invoiceId}`, {
    params: { kind },
    responseType: 'blob',
    timeout: 2 * 60 * 1000,
  });
  if (response.status === 202) return { pending: true };
  return { blob: response.data, fileName: fileNameFrom(response.headers['content-disposition'], 'SEF.pdf') };
};

/**
 * SEF registration and deletion date per PIB (from the daily SEF users list).
 * @param {string[]} pibs
 * @returns {Promise<Object>} `{ [pib]: { registered, deletionDate, name } }`
 */
export const getSefCompaniesStatus = async (pibs) => {
  const response = await axiosClient.post(`${API_BASE}/sef-companies/status`, { pibs });
  return response.data.data;
};

/** SEF channel use by day and module (tech admin). */
export const getUsage = async (days = 7) => {
  const response = await axiosClient.get(`${API_BASE}/usage`, { params: { days } });
  return response.data.data;
};
